import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import { api } from './api';
import './Calendar.css';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

const TZ = 'America/New_York';
const HOUR_HEIGHT = 56; // px per hour
const START_HOUR = 0;   // midnight
const END_HOUR = 24;    // full day
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);

const VIEW_CONFIG = {
  day: { label: 'Day', days: 1 },
  '3day': { label: '3 Day', days: 3 },
  week: { label: 'Week', days: 7 },
};

const SOURCE_LABELS = {
  zoom: 'Zoom',
  'google:frankap@gmail.com': 'Personal',
  'google:frank@padikkala.com': 'Padikkala',
  calsync: 'CalSync Blocks',
};

function isBackgroundEvent(event) {
  return event.display_layer === 'background' || event.role === 'derived_block';
}

function computeLayout(events) {
  // Returns Map: event.id -> { col, span, totalCols }
  // Events that don't overlap get full width (totalCols=1)
  // Events that overlap are split into side-by-side columns, and then each
  // event expands into any unused columns to its right. That second pass is
  // what prevents chained-but-not-simultaneous clusters from rendering as
  // needless slivers.
  const result = new Map();
  if (!events.length) return result;

  const sorted = [...events].sort((a, b) => a.start.localeCompare(b.start));
  const overlaps = (a, b) => a.start < b.end && b.start < a.end;

  let i = 0;
  while (i < sorted.length) {
    // Build a connected overlap group, then assign the smallest possible
    // number of columns within that group. Using the full group length makes
    // chained overlaps render as tiny slivers even when only 2-3 events
    // actually overlap at once.
    const group = [sorted[i]];
    let groupEnd = sorted[i].end;
    let j = i + 1;
    while (j < sorted.length && sorted[j].start < groupEnd) {
      group.push(sorted[j]);
      if (sorted[j].end > groupEnd) groupEnd = sorted[j].end;
      j++;
    }
    const columns = [];
    const assignments = [];
    group.forEach(evt => {
      const reusableCol = columns.findIndex(end => end <= evt.start);
      const col = reusableCol === -1 ? columns.length : reusableCol;
      columns[col] = evt.end;
      assignments.push({ evt, col });
    });

    const totalCols = Math.max(1, columns.length);
    assignments.forEach(({ evt, col }) => {
      let span = 1;
      for (let candidateCol = col + 1; candidateCol < totalCols; candidateCol += 1) {
        const blocked = assignments.some(other =>
          other.evt.id !== evt.id &&
          other.col === candidateCol &&
          overlaps(evt, other.evt)
        );
        if (blocked) break;
        span += 1;
      }
      result.set(evt.id, { col, span, totalCols });
    });
    i = j;
  }

  return result;
}

function EventBlock({ event, onClick, col, span = 1, totalCols, variant = 'foreground' }) {
  const start = dayjs(event.start).tz(TZ);
  const end   = dayjs(event.end).tz(TZ);
  const rawStartMins = (start.hour() - START_HOUR) * 60 + start.minute();
  const rawEndMins   = (end.hour() - START_HOUR) * 60 + end.minute();
  const dayMinutes = (END_HOUR - START_HOUR) * 60;
  const startMins = Math.max(0, rawStartMins);
  const endMins = Math.min(dayMinutes, rawEndMins <= 0 ? dayMinutes : rawEndMins);
  const duration  = Math.max(endMins - startMins, 15);
  const top    = (startMins / 60) * HOUR_HEIGHT;
  const height = (duration / 60) * HOUR_HEIGHT;

  if (rawStartMins >= dayMinutes || rawEndMins <= 0) return null;

  const colWidth = 100 / totalCols;
  const leftPct  = col * colWidth;
  const widthPct = span * colWidth;
  const isBackground = variant === 'background';
  const density = totalCols >= 4 ? 'crowded' : totalCols >= 3 ? 'dense' : 'normal';

  return (
    <div
      className="event-block"
      data-variant={variant}
      data-density={density}
      data-title={event.title}
      aria-label={`${event.title}, ${start.format('h:mm a')} to ${end.format('h:mm a')}`}
      role="button"
      tabIndex={0}
      style={{
        top, height: Math.max(height, 20),
        left: isBackground ? '4px' : `calc(${leftPct}% + 2px)`,
        width: isBackground ? 'calc(100% - 8px)' : `calc(${widthPct}% - 4px)`,
        borderLeft: `2.5px solid ${event.color}`,
        '--evt-color': event.color,
      }}
      onClick={() => onClick(event)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(event);
        }
      }}
    >
      {isBackground && (
        <div className="event-title background-title" title={event.title}>
          {event.title}
        </div>
      )}
      {!isBackground && <div className="event-title" title={event.title}>{event.title}</div>}
      {!isBackground && height > 30 && (
        <div className="event-time">
          {start.format('h:mm')}–{end.format('h:mma')}
        </div>
      )}
    </div>
  );
}

function EventModal({ event, onClose, onDeleteBlock, deleteLoading }) {
  if (!event) return null;
  const start = dayjs(event.start).tz(TZ);
  const end   = dayjs(event.end).tz(TZ);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-color-bar" style={{ background: event.color }} />
        <div className="modal-body">
          <div className="modal-title">{event.title}</div>
          <div className="modal-meta">
            <span>{start.format('ddd, MMM D · h:mm')}–{end.format('h:mma')}</span>
            <span className="modal-source">{SOURCE_LABELS[event.source] || event.source}</span>
          </div>
          {event.location && (
            <div className="modal-location">📍 {event.location.replace(/https?:\/\/[^\s]+/g, '').trim() || event.location}</div>
          )}
          {event.role === 'derived_block' && (
            <div className="modal-note">
              This is a CalSync-created availability block.
              {event.delete_action?.source_title ? ` Source: ${event.delete_action.source_title}.` : ''}
            </div>
          )}
          {event.is_deletable && (
            <button
              className="modal-delete"
              disabled={deleteLoading}
              onClick={() => onDeleteBlock(event)}
            >
              {deleteLoading ? 'Deleting...' : 'Delete CalSync block'}
            </button>
          )}
        </div>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>
    </div>
  );
}

function formatSyncDate(value) {
  if (!value) return '';
  return dayjs(value).tz(TZ).format('ddd M/D h:mma');
}

function SettingsPanel({
  user,
  calendars,
  loading,
  syncPlan,
  syncPlanLoading,
  syncPlanError,
  syncExecuteLoading,
  syncExecuteResult,
  skipKeywordInput,
  links,
  onClose,
  onConnect,
  onDisconnect,
  onRefresh,
  onRefreshSyncPlan,
  onExecuteSyncBatch,
  onSkipKeywordInputChange,
  onAddSkipKeyword,
  onDeleteSkipKeyword,
}) {
  const googleCalendars = calendars.filter(cal => cal.source === 'google');
  const zoomCalendar = calendars.find(cal => cal.id === 'zoom');
  const zoomStatus = zoomCalendar?.status || {};
  const summary = syncPlan?.summary || {};
  const nextBatch = syncPlan?.batch || [];
  const skipped = syncPlan?.skipped || [];
  const skipKeywords = syncPlan?.rules?.skip_keywords || [];
  const sourceSummary = syncPlan?.source_summary || {};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={e => e.stopPropagation()}>
        <div className="settings-header">
          <div>
            <div className="settings-title">Connected calendars</div>
            <div className="settings-subtitle">
              App login: {user?.email || 'frankap@gmail.com'}. Calendar source accounts stay connected server-side after approval.
            </div>
          </div>
          <button className="modal-close settings-close" onClick={onClose}>x</button>
        </div>

        <div className="settings-body">
          {loading && <div className="settings-loading">Checking connections...</div>}

          {zoomCalendar && (
            <div className="settings-card">
              <div className="settings-card-main">
                <span className="settings-dot" style={{ background: zoomCalendar.color }} />
                <div>
                  <div className="settings-card-title">{zoomCalendar.label}</div>
                  <div className="settings-card-meta">
                    Password is currently managed as a Railway secret. Cache {zoomCalendar.cache_ready ? 'ready' : 'not ready'}
                    {zoomCalendar.last_scan ? ` - last scan ${zoomCalendar.last_scan}` : ''}
                  </div>
                  {(zoomCalendar.sync_phase || zoomCalendar.last_error) && (
                    <div className={`settings-card-note ${zoomCalendar.last_error ? 'warning' : ''}`}>
                      {zoomCalendar.sync_phase ? `Phase: ${zoomCalendar.sync_phase}` : ''}
                      {zoomCalendar.last_error ? ` - ${zoomCalendar.last_error}` : ''}
                    </div>
                  )}
                  {zoomStatus.current_sync_token && (
                    <div className="settings-card-note">
                      Sync token reachable; baseline cache still required.
                    </div>
                  )}
                </div>
              </div>
              <span className={`settings-badge ${zoomCalendar.connected ? 'connected' : 'warning'}`}>
                {zoomCalendar.connected ? 'Configured' : 'Needs password'}
              </span>
            </div>
          )}

          {googleCalendars.map(cal => (
            <div className="settings-card" key={cal.id}>
              <div className="settings-card-main">
                <span className="settings-dot" style={{ background: cal.color }} />
                <div>
                  <div className="settings-card-title">{cal.label}</div>
                  <div className="settings-card-meta">
                    {cal.id} · {cal.server_token_present ? 'Railway token stored for background sync' : 'Railway token missing'}
                    {cal.service_ok === false ? ' · token needs reconnect' : ''}
                  </div>
                  {cal.token_status?.last_error && (
                    <div className="settings-card-note">
                      Last auth issue: {cal.token_status.last_error}
                    </div>
                  )}
                </div>
              </div>
              <div className="settings-actions">
                <span className={`settings-badge ${cal.server_token_present && cal.connected ? 'connected' : 'warning'}`}>
                  {cal.server_token_present && cal.connected ? 'Server connected' : 'Needs reconnect'}
                </span>
                <button className="settings-action-btn" onClick={() => onConnect(cal.id)}>
                  {cal.server_token_present && cal.connected ? 'Reconnect' : 'Connect'}
                </button>
                {cal.server_token_present && (
                  <button className="settings-action-btn subtle" onClick={() => onDisconnect(cal.id)}>
                    Disconnect
                  </button>
                )}
              </div>
            </div>
          ))}

          <section className="settings-section">
            <div className="settings-section-header">
              <div>
                <div className="settings-section-title">Google → Zoom sync preview</div>
                <div className="settings-section-subtitle">
                  Dry-run only for now. Blocks use 15 minutes before and after each event.
                </div>
              </div>
              <button className="settings-action-btn" onClick={onRefreshSyncPlan} disabled={syncPlanLoading}>
                {syncPlanLoading ? 'Checking...' : 'Refresh preview'}
              </button>
            </div>

            {syncPlanError && <div className="settings-inline-warning">{syncPlanError}</div>}
            {!syncPlanError && syncPlanLoading && <div className="settings-loading">Building sync preview...</div>}
            {!syncPlanError && syncPlan && (
              <>
                <div className="settings-plan-grid">
                  <div><strong>{summary.create || 0}</strong><span>new blocks</span></div>
                  <div><strong>{summary.update || 0}</strong><span>updates</span></div>
                  <div><strong>{summary.remove || 0}</strong><span>removals</span></div>
                  <div><strong>{summary.skipped || 0}</strong><span>skipped</span></div>
                  <div><strong>{summary.review || 0}</strong><span>review</span></div>
                </div>
                {syncPlan.sync_status?.updated_at && (
                  <div className="settings-sync-status">
                    Auto sync: {syncPlan.sync_status.phase || 'unknown'} · last checked {formatSyncDate(syncPlan.sync_status.updated_at)}
                    {syncPlan.sync_status.executed || syncPlan.sync_status.removed
                      ? ` · wrote ${syncPlan.sync_status.executed || 0}, removed ${syncPlan.sync_status.removed || 0}`
                      : ''}
                  </div>
                )}
                <div className="settings-source-summary">
                  {Object.entries(sourceSummary).map(([id, item]) => (
                    <div key={id} className="settings-source-summary-item">
                      <div className="settings-source-summary-title">
                        {item.label} <span>{item.connected ? 'connected' : 'needs reconnect'}</span>
                      </div>
                      <div className="settings-source-summary-meta">
                        seen {item.events_seen || 0} · new {item.create || 0} · updates {item.update || 0} · unchanged {item.unchanged || 0} · skipped {item.skipped || 0}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="settings-plan-group">
                  <div className="settings-mini-heading">Next batch</div>
                  {nextBatch.length === 0 ? (
                    <div className="settings-empty">Nothing queued for the next batch.</div>
                  ) : (
                    <div className="settings-plan-list">
                      {nextBatch.slice(0, 5).map(item => (
                        <div className="settings-plan-item" key={item.source_key}>
                          <div className="settings-plan-title">{item.source_title}</div>
                          <div className="settings-plan-meta">
                            {item.source_label} → {item.target_title} · {formatSyncDate(item.block_start)}–{formatSyncDate(item.block_end)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="settings-execute-row">
                  <button
                    className="settings-primary-action"
                    onClick={onExecuteSyncBatch}
                    disabled={syncExecuteLoading || nextBatch.length === 0}
                  >
                    {syncExecuteLoading ? 'Syncing next batch...' : `Run next ${Math.min(nextBatch.length, 5)} Zoom block${Math.min(nextBatch.length, 5) === 1 ? '' : 's'}`}
                  </button>
                  <div className="settings-execute-note">
                    Writes are capped at 5 per click and verified with a Zoom read-back.
                  </div>
                </div>

                {syncExecuteResult && (
                  <div className={`settings-execute-result ${syncExecuteResult.ok ? 'ok' : 'warning'}`}>
                    {syncExecuteResult.ok
                      ? `Synced ${syncExecuteResult.executed} block${syncExecuteResult.executed === 1 ? '' : 's'} and removed ${syncExecuteResult.removed || 0}.`
                      : `Sync stopped after ${syncExecuteResult.executed || 0} block${syncExecuteResult.executed === 1 ? '' : 's'} and ${syncExecuteResult.removed || 0} removals.`}
                    {syncExecuteResult.errors?.length ? ` ${syncExecuteResult.errors[0].error}` : ''}
                  </div>
                )}

                <div className="settings-plan-group">
                  <div className="settings-mini-heading">Skipped by rules</div>
                  {skipped.length === 0 ? (
                    <div className="settings-empty">No skipped events in this preview.</div>
                  ) : (
                    <div className="settings-plan-list compact">
                      {skipped.slice(0, 5).map(item => (
                        <div className="settings-plan-item" key={item.source_key}>
                          <div className="settings-plan-title">{item.source_title || item.source_key}</div>
                          <div className="settings-plan-meta">{item.reason}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="settings-keywords">
                    <div className="settings-mini-heading">Editable skip keywords</div>
                    <div className="settings-keyword-list">
                      {skipKeywords.map(keyword => (
                        <button
                          key={keyword}
                          className="settings-keyword-chip"
                          onClick={() => onDeleteSkipKeyword(keyword)}
                          title={`Remove ${keyword}`}
                        >
                          {keyword} <span>x</span>
                        </button>
                      ))}
                    </div>
                    <form className="settings-keyword-form" onSubmit={onAddSkipKeyword}>
                      <input
                        value={skipKeywordInput}
                        onChange={e => onSkipKeywordInputChange(e.target.value)}
                        placeholder="Add keyword, e.g. insurance"
                      />
                      <button className="settings-action-btn" type="submit">Add</button>
                    </form>
                    <div className="settings-keyword-help">
                      Click a chip to delete it. These rules affect future dry-runs and sync batches.
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>

          <section className="settings-section">
            <div className="settings-section-title">Helpful links</div>
            <div className="settings-link-list">
              {links.map(link => (
                <a key={link.href} className="settings-link" href={link.href} target="_blank" rel="noreferrer">
                  <span>{link.label}</span>
                  <span>{link.hint}</span>
                </a>
              ))}
            </div>
          </section>
        </div>

        <div className="settings-footer">
          <button className="refresh-btn" onClick={onRefresh}>Refresh status</button>
        </div>
      </div>
    </div>
  );
}

export default function Calendar({ user, onLogout }) {
  const [anchorDate, setAnchorDate] = useState(() => dayjs().tz(TZ).startOf('day'));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hiddenSources, setHiddenSources] = useState(new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [authRequired, setAuthRequired] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [syncPlan, setSyncPlan] = useState(null);
  const [syncPlanLoading, setSyncPlanLoading] = useState(false);
  const [syncPlanError, setSyncPlanError] = useState(null);
  const [syncExecuteLoading, setSyncExecuteLoading] = useState(false);
  const [syncExecuteResult, setSyncExecuteResult] = useState(null);
  const [skipKeywordInput, setSkipKeywordInput] = useState('');
  const [view, setView] = useState(() =>
    window.matchMedia?.('(max-width: 700px)').matches ? '3day' : 'week'
  );
  const [deleteBlockLoading, setDeleteBlockLoading] = useState(false);
  const headerScrollRef = useRef(null);
  const gridScrollRef = useRef(null);

  const viewDays = VIEW_CONFIG[view].days;
  const rangeStart = view === 'week' ? anchorDate.startOf('week') : anchorDate.startOf('day');
  const rangeEnd = rangeStart.add(viewDays, 'day');
  const rangeStartIso = rangeStart.toISOString();
  const rangeEndIso = rangeEnd.toISOString();
  const days = Array.from({ length: viewDays }, (_, i) => rangeStart.add(i, 'day'));
  const dayColMinWidth = view === 'day' ? 320 : view === '3day' ? 240 : 190;
  const calendarGridTemplate = `var(--time-gutter-width) repeat(${days.length}, minmax(${dayColMinWidth}px, 1fr))`;
  const rangeLabel = viewDays === 1
    ? rangeStart.format('MMM D, YYYY')
    : `${rangeStart.format('MMM D')}–${rangeEnd.subtract(1, 'day').format('MMM D, YYYY')}`;

  const syncHorizontalScroll = useCallback((sourceRef, targetRef) => {
    if (!sourceRef.current || !targetRef.current) return;
    targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data  = await api.events(rangeStartIso, rangeEndIso);
      setEvents(data.events || []);
      setAuthRequired(data.auth_required || []);
      setWarnings(data.warnings || []);
    } catch (e) {
      if (e.status === 401) {
        api.login();
      } else {
        setError('Failed to load events.');
      }
    } finally {
      setLoading(false);
    }
  }, [rangeStartIso, rangeEndIso]);

  const fetchCalendars = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const data = await api.calendars();
      setCalendars(data.calendars || []);
    } catch (e) {
      setCalendars([]);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const fetchSyncPlan = useCallback(async () => {
    setSyncPlanLoading(true);
    setSyncPlanError(null);
    try {
      const data = await api.syncPlan(10);
      setSyncPlan(data);
    } catch (e) {
      setSyncPlan(null);
      if (e.status === 401) {
        setSyncPlanError('Sign in or reconnect Google calendars to preview sync blocks.');
      } else {
        setSyncPlanError(e.message || e.error || 'Failed to load sync preview.');
      }
    } finally {
      setSyncPlanLoading(false);
    }
  }, []);

  const updateSkipKeywords = useCallback(async (updater) => {
    const current = syncPlan?.rules?.skip_keywords || [];
    const next = typeof updater === 'function' ? updater(current) : updater;
    setSyncPlanError(null);
    try {
      await api.updateSkipKeywords(next);
      await fetchSyncPlan();
    } catch (e) {
      setSyncPlanError(e.message || e.error || 'Failed to update skip keywords.');
    }
  }, [syncPlan, fetchSyncPlan]);

  const addSkipKeyword = useCallback(async (event) => {
    event.preventDefault();
    const keyword = skipKeywordInput.trim().toLowerCase();
    if (!keyword) return;
    await updateSkipKeywords(current => {
      if (current.includes(keyword)) return current;
      return [...current, keyword];
    });
    setSkipKeywordInput('');
  }, [skipKeywordInput, updateSkipKeywords]);

  const deleteSkipKeyword = useCallback(async (keyword) => {
    await updateSkipKeywords(current => current.filter(item => item !== keyword));
  }, [updateSkipKeywords]);

  const executeSyncBatch = useCallback(async () => {
    setSyncExecuteLoading(true);
    setSyncExecuteResult(null);
    setSyncPlanError(null);
    try {
      const localToday = dayjs().tz(TZ).startOf('day').toISOString();
      const data = await api.executeGoogleToZoomSync({
        start: localToday,
        end: dayjs().tz(TZ).add(14, 'day').toISOString(),
        limit: 5,
      });
      setSyncExecuteResult(data);
      await fetchSyncPlan();
      await fetchEvents();
    } catch (e) {
      setSyncExecuteResult({ ok: false, executed: e.executed || 0, errors: [{ error: e.error || e.message || 'Sync failed.' }] });
    } finally {
      setSyncExecuteLoading(false);
    }
  }, [fetchEvents, fetchSyncPlan]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchCalendars(); }, [fetchCalendars]);
  const disconnectCalendar = async (calendarId) => {
    await api.disconnectCalendar(calendarId);
    await fetchCalendars();
    await fetchEvents();
  };

  const deleteCalsyncBlock = async (event) => {
    if (!event?.delete_action) return;
    setDeleteBlockLoading(true);
    setError(null);
    try {
      await api.deleteCalsyncBlock(event.delete_action);
      setSelectedEvent(null);
      await fetchEvents();
    } catch (e) {
      setError(e.message || e.error || 'Failed to delete CalSync block.');
    } finally {
      setDeleteBlockLoading(false);
    }
  };

  const toggleSource = (source) => {
    setHiddenSources(prev => {
      const next = new Set(prev);
      next.has(source) ? next.delete(source) : next.add(source);
      return next;
    });
  };

  const sources = [
    { id: 'zoom', label: 'Zoom Work', color: '#1D9E75' },
    { id: 'google:frankap@gmail.com', label: 'Personal', color: '#7F77DD' },
    { id: 'google:frank@padikkala.com', label: 'Padikkala', color: '#D85A30' },
    { id: 'calsync', label: 'CalSync Blocks', color: '#64748b' },
  ];

  const visibleEvents = events.filter(e => !hiddenSources.has(e.source));

  const eventsForDay = (day) =>
    visibleEvents.filter(e => dayjs(e.start).tz(TZ).isSame(day, 'day'));

  const today = dayjs().tz(TZ);
  const nowMinutes = (today.hour() - START_HOUR) * 60 + today.minute();
  const nowTop = (nowMinutes / 60) * HOUR_HEIGHT;

  return (
    <div className="cal-root">
      {/* Header */}
      <header className="cal-header">
        <div className="cal-header-left">
          <span className="cal-brand">CalSync</span>
          <button className="nav-btn" onClick={() => setAnchorDate(d => d.subtract(viewDays, 'day'))}>‹</button>
          <button className="nav-btn today-btn" onClick={() => setAnchorDate(dayjs().tz(TZ).startOf('day'))}>Today</button>
          <button className="nav-btn" onClick={() => setAnchorDate(d => d.add(viewDays, 'day'))}>›</button>
          <span className="cal-range">{rangeLabel}</span>
        </div>
        <div className="cal-header-right">
          <div className="source-filters">
            {sources.map(s => (
              <button
                key={s.id}
                className={`source-pill ${hiddenSources.has(s.id) ? 'hidden' : ''}`}
                style={{ '--pill-color': s.color }}
                onClick={() => toggleSource(s.id)}
              >
                <span className="pill-dot" />
                {s.label}
              </button>
            ))}
          </div>
          <div className="view-switcher">
            {Object.entries(VIEW_CONFIG).map(([key, config]) => (
              <button
                key={key}
                className={view === key ? 'active' : ''}
                onClick={() => setView(key)}
              >
                {config.label}
              </button>
            ))}
          </div>
          <button className="settings-btn" onClick={() => { setSettingsOpen(true); fetchCalendars(); fetchSyncPlan(); }}>
            Settings
          </button>
          <button className="logout-btn" onClick={onLogout} title="Sign out">↩</button>
        </div>
      </header>

      {/* Day headers */}
      <div
        className="day-headers"
        ref={headerScrollRef}
        onScroll={() => syncHorizontalScroll(headerScrollRef, gridScrollRef)}
        style={{ gridTemplateColumns: calendarGridTemplate }}
      >
        <div className="time-gutter-head" />
        {days.map(day => (
          <div key={day.toString()} className={`day-head ${day.isSame(today, 'day') ? 'today' : ''}`}>
            <span className="day-name">{day.format('ddd')}</span>
            <span className={`day-num ${day.isSame(today, 'day') ? 'today-num' : ''}`}>
              {day.format('D')}
            </span>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div
        className="cal-grid-wrap"
        ref={gridScrollRef}
        onScroll={() => syncHorizontalScroll(gridScrollRef, headerScrollRef)}
      >
        {loading && <div className="loading-bar" />}
        {authRequired.length > 0 && (
          <div className="connection-banner">
            <div>
              <div className="connection-title">Connect calendars</div>
              <div className="connection-subtitle">
                Your app login is active, but one or more source calendars need server-side reconnect.
              </div>
            </div>
            <div className="connection-actions">
              <button className="connect-btn" onClick={() => { setSettingsOpen(true); fetchCalendars(); fetchSyncPlan(); }}>
                Open Settings
              </button>
            </div>
          </div>
        )}
        <div className="cal-grid" style={{ gridTemplateColumns: calendarGridTemplate }}>
          {/* Time gutter */}
          <div className="time-gutter">
            {HOURS.map(h => (
              <div key={h} className="hour-label" style={{ height: HOUR_HEIGHT }}>
                {dayjs().hour(h).minute(0).format('h a')}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day, di) => (
            <div key={day.toString()} className={`day-col ${day.isSame(today, 'day') ? 'today-col' : ''}`}>
              {HOURS.map(h => (
                <div key={h} className="hour-cell" style={{ height: HOUR_HEIGHT }} />
              ))}
              {/* Now line */}
              {day.isSame(today, 'day') && nowTop > 0 && nowTop < (END_HOUR - START_HOUR) * HOUR_HEIGHT && (
                <div className="now-line" style={{ top: nowTop }} />
              )}
              {/* Events */}
              {(() => {
                const dayEvts = eventsForDay(day);
                const backgroundEvts = dayEvts.filter(isBackgroundEvent);
                const foregroundEvts = dayEvts.filter(evt => !isBackgroundEvent(evt));
                const layout = computeLayout(foregroundEvts);
                return [
                  ...backgroundEvts.map(evt => (
                    <EventBlock
                      key={`${evt.source}:${evt.target_calendar || ''}:${evt.id}`}
                      event={evt}
                      onClick={setSelectedEvent}
                      col={0}
                      span={1}
                      totalCols={1}
                      variant="background"
                    />
                  )),
                  ...foregroundEvts.map(evt => {
                  const pos = layout.get(evt.id) || { col: 0, span: 1, totalCols: 1 };
                  return (
                    <EventBlock
                      key={`${evt.source}:${evt.target_calendar || ''}:${evt.id}`}
                      event={evt}
                      onClick={setSelectedEvent}
                      col={pos.col}
                      span={pos.span}
                      totalCols={pos.totalCols}
                    />
                  );
                })];
              })()}
            </div>
          ))}
        </div>
      </div>

      {/* Status bar */}
      <div className="status-bar">
        <span>{events.length} events loaded</span>
        {error && <span className="status-error">{error}</span>}
        {!error && warnings.length > 0 && <span className="status-warning">{warnings.join(' · ')}</span>}
        <button className="refresh-btn" onClick={fetchEvents}>↻ Refresh</button>
      </div>

      <EventModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onDeleteBlock={deleteCalsyncBlock}
        deleteLoading={deleteBlockLoading}
      />
      {settingsOpen && (
        <SettingsPanel
          user={user}
          calendars={calendars}
          loading={settingsLoading}
          syncPlan={syncPlan}
          syncPlanLoading={syncPlanLoading}
          syncPlanError={syncPlanError}
          syncExecuteLoading={syncExecuteLoading}
          syncExecuteResult={syncExecuteResult}
          skipKeywordInput={skipKeywordInput}
          links={[
            { label: 'App health', hint: 'public status JSON', href: api.url('/health') },
            { label: 'Visible events API', hint: 'current calendar range', href: api.url(`/api/events?start=${encodeURIComponent(rangeStartIso)}&end=${encodeURIComponent(rangeEndIso)}`) },
            { label: 'Sync preview JSON', hint: 'Google → Zoom dry-run', href: api.url('/api/sync/google-to-zoom/plan?limit=10') },
            { label: 'Connect Personal Google', hint: 'frankap@gmail.com', href: api.url('/auth/calendar/frankap@gmail.com') },
            { label: 'Connect Padikkala Google', hint: 'frank@padikkala.com', href: api.url('/auth/calendar/frank@padikkala.com') },
          ]}
          onClose={() => setSettingsOpen(false)}
          onConnect={api.authCalendar}
          onDisconnect={disconnectCalendar}
          onRefresh={fetchCalendars}
          onRefreshSyncPlan={fetchSyncPlan}
          onExecuteSyncBatch={executeSyncBatch}
          onSkipKeywordInputChange={setSkipKeywordInput}
          onAddSkipKeyword={addSkipKeyword}
          onDeleteSkipKeyword={deleteSkipKeyword}
        />
      )}
    </div>
  );
}
